import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';
import { activateAuthLayout } from '../../../store/actions';
import { getQuoteBuilderURL } from '../../../helpers/common';

const SEGMENT_FIELDS = gql`
  fragment SegmentFields on ReportingSegment {
    Count
    MonthlyRevenue
    TotalValue
    Quotes {
      QuoteId
      ProposalNumber
      CompanyName
      WorkflowStep
      MonthlyRevenue
      TotalValue
      ContractMonths
      IsAveraged
    }
  }
`;

const REPORTING_STATS = gql`
  ${SEGMENT_FIELDS}
  query ReportingStats($endDate: String) {
    ReportingStats(endDate: $endDate) {
      Prepared { ...SegmentFields }
      Accepted { ...SegmentFields }
      SentForSigning { ...SegmentFields }
      Pending { ...SegmentFields }
    }
  }
`;

const currency = (val) =>
  val == null ? '—' : '$' + Number(val).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Segment = ({ title, data, period }) => {
  const history = useHistory();
  if (!data) return null;
  return (
    <Card className="mb-4">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="my-0">{title}</h5>
            <small className="text-muted">Previous {period} days</small>
          </div>
          <span className="badge badge-primary badge-pill px-3 py-2">{data.Count}</span>
        </div>
        {data.Quotes.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="thead-dark">
                <tr>
                  <th>Quote</th>
                  <th>Company</th>
                  <th className="text-right">MRR</th>
                  <th className="text-right">Term</th>
                  <th className="text-right">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {data.Quotes.map((q) => (
                  <tr key={q.QuoteId} style={{ cursor: 'pointer' }} onClick={() => history.push(getQuoteBuilderURL(q.WorkflowStep, q.QuoteId))}>
                    <td>{q.ProposalNumber}</td>
                    <td>{q.CompanyName}{q.IsAveraged && <span className="text-danger ml-1">*</span>}</td>
                    <td className="text-right">{currency(q.MonthlyRevenue)}</td>
                    <td className="text-right">{!q.IsAveraged ? q.ContractMonths : '—'}</td>
                    <td className="text-right">{currency(q.TotalValue)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-weight-bold">
                  <td colSpan="2">Total</td>
                  <td className="text-right">{currency(data.MonthlyRevenue)}</td>
                  <td></td>
                  <td className="text-right">{currency(data.TotalValue)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {data.Quotes.some(q => q.IsAveraged) && (
          <small className="text-muted mt-2 d-block"><span className="text-danger">*</span> Values are the average of the two quote options as the quote has not been accepted</small>
        )}
      </CardBody>
    </Card>
  );
};

const Reporting = ({ activateAuthLayout }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const { data, loading } = useQuery(REPORTING_STATS, { variables: { endDate } });
  const stats = data?.ReportingStats;

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="page-title-box">
          <Row className="align-items-center">
            <Col sm="6">
              <h4 className="page-title">Reporting</h4>
            </Col>
            <Col sm="6" className="text-right">
              <input type="date" className="form-control d-inline-block w-auto" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </Col>
          </Row>
        </div>
        {stats && (
          <Row style={{ opacity: loading ? 0.4 : 1 }}>
            <Col lg="6">
              <Segment title="Quotes prepared" period={7} data={stats.Prepared} />
              <Segment title="Quotes accepted" period={7} data={stats.Accepted} />
              <Segment title="Sent for signing" period={7} data={stats.SentForSigning} />
            </Col>
            <Col lg="6">
              <Segment title="Pending quotes" period={28} data={stats.Pending} />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default connect(null, { activateAuthLayout })(Reporting);
